import React, {useState} from 'react';
import {Box, Typography, TextField, Button, Chip} from '@mui/material';
import {useGetMp3FileComponent} from '../../../hooks/productions/useProductions';
import {GetMp3FileComponentRequestParam} from '../../../api/types/production.type';
import ArrButton from '../../../components/common/arr-button/ArrButton';

const Mp3FileComponentApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(1017);
  const [parentSetId, setParentSetId] = useState<number>(1);
  const [moduleType, setModuleType] = useState<string>('Production');
  const [exportProductionId, setExportProductionId] = useState<number>(0);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  // ✅ Hook will automatically fetch when enabled is true
  const {
    data: mp3ComponentData,
    isLoading,
    error,
    refetch,
  } = useGetMp3FileComponent(
    {
      SecurityGroupId: securityGroupId,
      moduletype: moduleType,
      parentsetid: parentSetId,
      productionId: productionId,
      exportProductionId: exportProductionId,
    } as GetMp3FileComponentRequestParam,
    enabled // Only fetch when user clicks "Test API"
  );

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (mp3ComponentData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(mp3ComponentData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 MP3 File Component API Test
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGroupId}
            onChange={(e) => setSecurityGroupId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Module Type"
            type="text"
            value={moduleType}
            onChange={(e) => setModuleType(e.target.value)}
            size="small"
            placeholder="Production"
          />
          <TextField
            label="Parent Set ID"
            type="number"
            value={parentSetId}
            onChange={(e) => setParentSetId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Production ID"
            type="number"
            value={productionId}
            onChange={(e) => setProductionId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Export Production ID"
            type="number"
            value={exportProductionId}
            onChange={(e) => setExportProductionId(Number(e.target.value))}
            size="small"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            data-testid="mp3file-component-api-test-test-api-button">
            🚀 Test API
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            data-testid="mp3file-component-api-test-reset-button">
            🔄 Reset
          </Button>
          {enabled && (
            <Button
              variant="outlined"
              onClick={() => refetch()}
              data-testid="mp3file-component-api-test-refetch-button">
              ♻️ Refetch
            </Button>
          )}
        </Box>
      </Box>
      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Loading... Making API request...
          </Typography>
        </Box>
      )}
      {/* Error State */}
      {error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {error.message}
          </Typography>
        </Box>
      )}
      {/* Success State - Display Response */}
      {mp3ComponentData && !isLoading && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ API Response
          </Typography>

          {/* Summary */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size">
              📊 Response Summary
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', marginTop: 1}}>
              <Chip
                label={`Produce Load File: ${mp3ComponentData.produceLoadFile ? 'Yes' : 'No'}`}
                color={mp3ComponentData.produceLoadFile ? 'success' : 'default'}
                size="small"
              />
              <Chip
                label={`Redaction Type: ${mp3ComponentData.redactionType}`}
                color="primary"
                size="small"
              />
              <Chip
                label={`Burn Redaction: ${mp3ComponentData.isBurnRedactionSelected ? 'Yes' : 'No'}`}
                color={
                  mp3ComponentData.isBurnRedactionSelected
                    ? 'success'
                    : 'default'
                }
                size="small"
              />
              <Chip
                label={`Redaction by Annotation: ${mp3ComponentData.isRedactionByAnnotation ? 'Yes' : 'No'}`}
                color={
                  mp3ComponentData.isRedactionByAnnotation ? 'info' : 'default'
                }
                size="small"
              />
            </Box>
            <Typography className="typo-text body3_size" sx={{marginTop: 1}}>
              • Filler Audio ID:{' '}
              {mp3ComponentData.fillerAudioID || 'Not specified'}
            </Typography>
            <Typography className="typo-text body3_size">
              • Redaction Tag List:{' '}
              {mp3ComponentData.specifiedRedactionTagList || 'None'}
            </Typography>
          </Box>

          {/* Full JSON Response */}
          <Box sx={{marginTop: 2}}>
            <Box sx={{marginBottom: 1}}>
              <ArrButton
                variant={copySuccess ? 'secondary' : 'primary'}
                size="small"
                onClick={handleCopyJson}
                dataTestId="mp3file-component-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </ArrButton>
            </Box>
            <Box
              sx={{
                padding: 2,
                backgroundColor: '#263238',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '500px',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{color: '#fff', marginBottom: 1}}
              >
                📄 Full JSON Response
              </Typography>
              <pre
                style={{
                  color: '#4caf50',
                  fontSize: '12px',
                  margin: 0,
                  fontFamily: 'monospace',
                }}
              >
                {JSON.stringify(mp3ComponentData, null, 2)}
              </pre>
            </Box>
          </Box>

          {/* Detailed Breakdown */}
          <Box sx={{marginTop: 3}}>
            <Typography variant="h6" className="typo-text subhead3_size">
              📋 MP3 Configuration Details
            </Typography>

            <Box
              sx={{
                marginTop: 2,
                padding: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
              }}
            >
              <Typography className="typo-text label2_size" gutterBottom>
                Configuration Settings
              </Typography>

              <Box
                sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}
              >
                <Typography className="typo-text body3_size">
                  <strong>Produce Load File:</strong>{' '}
                  {mp3ComponentData.produceLoadFile ? 'Yes' : 'No'}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Redaction Type:</strong>{' '}
                  {mp3ComponentData.redactionType}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Burn Redaction Selected:</strong>{' '}
                  {mp3ComponentData.isBurnRedactionSelected ? 'Yes' : 'No'}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Redaction by Annotation:</strong>{' '}
                  {mp3ComponentData.isRedactionByAnnotation ? 'Yes' : 'No'}
                </Typography>
              </Box>

              <Box sx={{marginTop: 2}}>
                <Typography className="typo-text body3_size">
                  <strong>Filler Audio ID:</strong>{' '}
                  {mp3ComponentData.fillerAudioID || 'N/A'}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Specified Redaction Tag List:</strong>{' '}
                  {mp3ComponentData.specifiedRedactionTagList || 'None'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {/* Initial State or No Data */}
      {!isLoading && !mp3ComponentData && !error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ff9800',
          }}
        >
          <Typography className="typo-text body2_size">
            {!enabled
              ? '👆 Click "Test API" button to make the API request'
              : '⏳ Preparing request...'}
          </Typography>
        </Box>
      )}
      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong>{' '}
          /productions/api/v1/production/view/mp3filecomponent
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> GET
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Query Params:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`SecurityGroupId=${securityGroupId}
moduletype=${moduleType}
parentsetid=${parentSetId}
productionId=${productionId}
exportProductionId=${exportProductionId}`}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
      </Box>
    </Box>
  );
};

export default Mp3FileComponentApiTest;
